import type { OpenPanel } from '@openpanel/web';

/**
 * Product events to OpenPanel (project «Ватман» on openpanel.slicetech.ru). Where they go comes
 * from the server (`/api/client-config`); without it nothing is sent. The profile is the user id
 * only: no names or addresses leave for analytics. The server reports what it alone knows for
 * sure (accounts, things created or changed, also by agents); here — how the interface is used.
 * Screen views are tracked on every route change.
 */

type Props = Record<string, string | number | boolean | null | undefined>;

let op: OpenPanel | null = null;
let starting: Promise<void> | null = null;
/** Events before the client is ready (the config comes in a moment after start). */
const early: [string, Props | undefined][] = [];
let profile: string | null = null;

/** Analytics never surfaces an error. */
const quiet = (p: Promise<unknown> | undefined) => void p?.catch(() => {});

export function initAnalytics() {
  if (starting || typeof window === 'undefined') return;
  starting = fetch('/api/client-config', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then(async (cfg: { openpanel?: { apiUrl: string; clientId: string } | null; env?: string } | null) => {
      if (!cfg?.openpanel) return;
      // The SDK loads only when there is somewhere to send to.
      const { OpenPanel } = await import('@openpanel/web');
      op = new OpenPanel({
        apiUrl: cfg.openpanel.apiUrl,
        clientId: cfg.openpanel.clientId,
        trackScreenViews: true,
        trackOutgoingLinks: true,
        trackAttributes: false,
      });
      op.setGlobalProperties({ app: 'web', env: cfg.env ?? 'dev' });
      if (profile) quiet(op.identify({ profileId: profile }));
      for (const [name, props] of early.splice(0)) quiet(op.track(name, props));
    })
    .catch(() => {});
}

/** One product event; never throws, never waits. */
export function track(name: string, props?: Props) {
  if (op) quiet(op.track(name, props));
  else if (early.length < 100) early.push([name, props]);
}

/** Links events to the signed-in person (by id), or forgets them after a sign-out. */
export function setProfile(id: string | null) {
  if (id === profile) return;
  if (!id && profile) op?.clear();
  profile = id;
  if (id && op) quiet(op.identify({ profileId: id }));
}

/**
 * The first time something happens in this page's life: "tool_used" for each tool once per
 * board visit instead of on every click.
 */
const seen = new Set<string>();
export function trackOnce(key: string, name: string, props?: Props) {
  if (seen.has(key)) return;
  seen.add(key);
  track(name, props);
}

/** Forgets what `trackOnce` saw (a new board opened). */
export function resetOnce(prefix: string) {
  for (const k of [...seen]) if (k.startsWith(prefix)) seen.delete(k);
}

/** Items this person created on the open board, by type: a summary when they leave it. */
const created = new Map<string, number>();

/** Called by the board document for every item created here (not ones that arrive from others). */
export function countCreated(type: string) {
  created.set(type, (created.get(type) ?? 0) + 1);
  trackOnce(`board:item:${type}`, 'item_created', { type });
}

/** What was created since the last call, as event properties (`created_sticky: 3`…). */
export function takeCreated(): Props {
  const out: Props = { created: [...created.values()].reduce((a, b) => a + b, 0) };
  for (const [type, n] of created) out[`created_${type}`] = n;
  created.clear();
  return out;
}

/** Script errors, each message once per page: what breaks for people, by page. */
export function trackErrors() {
  const report = (message: string) => {
    const m = message.slice(0, 160);
    trackOnce(`error:${m}`, 'client_error', { message: m, page: location.pathname.split('/').slice(0, 2).join('/') });
  };
  window.addEventListener('error', (e) => report(String(e.message || e.error || 'error')));
  window.addEventListener('unhandledrejection', (e) => report(String((e.reason as Error | undefined)?.message ?? e.reason ?? 'rejection')));
}
